import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Hero = () => {
  const [counters, setCounters] = useState({ schools: 0, students: 0, uptime: 0 });

  useEffect(() => {
    const animateCounters = () => {
      const targets = { schools: 500, students: 50000, uptime: 99.9 };
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;

      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        
        setCounters({
          schools: Math.floor(targets.schools * progress),
          students: Math.floor(targets.students * progress),
          uptime: Math.min(targets.uptime, (targets.uptime * progress).toFixed(1))
        });

        if (step >= steps) clearInterval(timer);
      }, stepTime);
    };

    const timer = setTimeout(animateCounters, 1000);
    return () => clearTimeout(timer);
  }, []);

  const chartBars = [70, 85, 60, 95, 75];

  return (
    <section className="hero">
      <div className="hero-bg">
        <motion.div 
          className="bg-blur blur-1"
          animate={{ y: [-20, 20, -20] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div 
          className="bg-blur blur-2"
          animate={{ y: [20, -20, 20] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div 
          className="bg-blur blur-3"
          animate={{ y: [-15, 15, -15] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
      </div>
      
      <div className="hero-container">
        <motion.div 
          className="hero-content"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div 
            className="hero-badge"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring" }}
          >
            <span className="badge-dot"></span>
            Revolutionary School Management Platform
          </motion.div>
          
          <motion.h1 
            className="hero-title"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            Transform Education with
            <span className="title-gradient"> Smart Technology</span>
          </motion.h1>
          
          <motion.p 
            className="hero-desc"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            Streamline your school operations with our comprehensive management system. 
            From student tracking to parent communication - everything in one powerful platform.
          </motion.p>
          
          <motion.div 
            className="hero-actions"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
          >
            <Link to="/pricing" className="btn btn-primary">
              <span>Start Free Trial</span>
              <i className="fas fa-rocket"></i>
            </Link>
            <Link to="/about" className="btn btn-secondary">
              <i className="fas fa-play-circle"></i>
              <span>Watch Demo</span>
            </Link>
          </motion.div>
          
          <motion.div 
            className="hero-stats"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
          >
            <div className="stat">
              <div className="stat-num">{counters.schools}+</div>
              <div className="stat-text">Schools</div>
            </div>
            <div className="stat">
              <div className="stat-num">{(counters.students / 1000).toFixed(0)}K+</div>
              <div className="stat-text">Students</div>
            </div>
            <div className="stat">
              <div className="stat-num">{counters.uptime}%</div>
              <div className="stat-text">Uptime</div>
            </div>
          </motion.div>
        </motion.div>
        
        <motion.div 
          className="hero-visual"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="hero-visual">
            <motion.div 
              className="dashboard"
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 6, repeat: Infinity }}
            >
              <div className="dashboard-header">
                <div className="header-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="header-title">Vidhyalaya Dashboard</div>
              </div>
              <div className="dashboard-content">
                <div className="dashboard-nav">
                  <div className="nav-item active"></div>
                  <div className="nav-item"></div>
                  <div className="nav-item"></div>
                  <div className="nav-item"></div>
                </div>
                <div className="dashboard-main">
                  <div className="main-stats">
                    <div className="stat-box"></div>
                    <div className="stat-box"></div>
                    <div className="stat-box"></div>
                  </div>
                  <div className="main-chart">
                    {chartBars.map((height, index) => (
                      <motion.div
                        key={index}
                        className="chart-bar"
                        style={{ height: `${height}%` }}
                        initial={{ height: 0 }}
                        animate={{ height: `${height}%` }}
                        transition={{ delay: 1.5 + index * 0.1, duration: 0.5 }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
            
            <div className="floating-elements">
              <motion.div 
                className="float-item item-1"
                animate={{ y: [-15, 15, -15] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <i className="fas fa-graduation-cap"></i>
              </motion.div>
              <motion.div 
                className="float-item item-2"
                animate={{ y: [15, -15, 15] }}
                transition={{ duration: 5, repeat: Infinity }}
              >
                <i className="fas fa-chart-bar"></i>
              </motion.div>
              <motion.div 
                className="float-item item-3"
                animate={{ y: [-10, 10, -10] }}
                transition={{ duration: 6, repeat: Infinity }}
              >
                <i className="fas fa-users"></i>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;